import React, { useState } from 'react';
import Modal from 'react-modal';
import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import '../ExternalUsers/CreateExternalModal.css';

const GenerateReport = ({ isOpen, onRequestClose, onReportCreated }) => {
    const [reportType, setReportType] = useState('Appointments');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        console.log("hello")

        try {
            // Step 1: Create the report on the server using a POST request
            const createReportResponse = await fetch("https://api.hgcdental.clinic/api/reports", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    reportType,
                    reportName: `${reportType} Report from ${startDate} to ${endDate}`,
                    dateFrom: startDate,
                    dateTo: endDate,
                    isArchived: false,
                }),
            });

            if (!createReportResponse.ok) {
                throw new Error("Failed to create report on the server.");
            }

            const newReport = await createReportResponse.json();

            // Step 2: Notify the parent component if onReportCreated is provided
            if (typeof onReportCreated === 'function') {
                onReportCreated(newReport);
            }

            // Step 3: Fetch data and generate the Excel report
            await handleGenerateReport();
        } catch (err) {
            console.error("Error creating or generating report:", err);
            setError(err.message || "An error occurred while creating or generating the report.");
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = async () => {
        try {
            // Validate dates for Appointments, Treatments, and Dentists report types
            if (
                (reportType === 'Appointments' || reportType === 'Treatments' || reportType === 'Dentists') &&
                (!startDate || !endDate)
            ) {
                setError("Please select both start and end dates.");
                return;
            }

            let reportData;

            if (reportType === 'Appointments') {
                // Fetch appointment data
                const response = await fetch(
                    `https://api.hgcdental.clinic/api/appointments?start=${startDate}&end=${endDate}`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch appointments.");
                }

                const data = await response.json();

                if (!data || data.length === 0) {
                    setError("No appointments found for the selected date range.");
                    return;
                }

                // Filter appointments by date range (client-side verification)
                const filteredData = data.filter(appointment => {
                    const appointmentDate = appointment.date.split("T")[0];
                    return appointmentDate >= startDate && appointmentDate <= endDate;
                });

                if (filteredData.length === 0) {
                    setError("No appointments found for the selected date range after filtering.");
                    return;
                }

                // Group appointments by date
                const groupedData = {};
                filteredData.forEach((appointment) => {
                    const appointmentDate = appointment.date.split("T")[0];
                    if (!groupedData[appointmentDate]) {
                        groupedData[appointmentDate] = {
                            totalAppointments: 0,
                            revenue: 0,
                        };
                    }
                    groupedData[appointmentDate].totalAppointments++;

                    if (appointment.invoice && Array.isArray(appointment.invoice)) {
                        const invoiceTotal = appointment.invoice.reduce(
                            (sum, item) => sum + item.treatmentCost * item.quantity,
                            0
                        );
                        groupedData[appointmentDate].revenue += invoiceTotal;
                    }
                });

                reportData = Object.entries(groupedData).map(([date, summary]) => ({
                    date,
                    totalAppointments: summary.totalAppointments,
                    revenue: summary.revenue,
                }));
            } else if (reportType === 'Patients') {
                // Fetch patient data (no date filtering needed)
                const response = await fetch('https://api.hgcdental.clinic/api/patients');
            
                if (!response.ok) {
                    throw new Error("Failed to fetch patients.");
                }
            
                const data = await response.json();
            
                if (!data || data.length === 0) {
                    setError("No patients found.");
                    return;
                }
            
                // Filter out archived patients
                const activePatients = data.filter(patient => !patient.isArchived);
            
                if (activePatients.length === 0) {
                    setError("No active patients found (all patients are archived).");
                    return;
                }
            
                reportData = activePatients.map((patient) => ({
                    patientID: patient.patientID,
                    name: patient.name,
                    sex: patient.sex,
                    birthdate: format(new Date(patient.birthdate), 'yyyy-MM-dd'),
                    ageYear: patient.ageYear,
                    ageMonth: patient.ageMonth,
                    guardianName: patient.guardianName,
                    address: patient.address,
                    ContactInformation: patient.ContactInformation,
                    email: patient.email,
                }));
            } else if (reportType === 'Treatments') {
                // Fetch appointment data and filter by date
                const response = await fetch(
                    `https://api.hgcdental.clinic/api/appointments?start=${startDate}&end=${endDate}`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch appointments.");
                }

                const data = await response.json();

                if (!data || data.length === 0) {
                    setError("No appointments found for the selected date range.");
                    return;
                }

                // Filter by date (client-side)
                const filteredData = data.filter(appointment => {
                    const appointmentDate = appointment.date.split("T")[0];
                    return appointmentDate >= startDate && appointmentDate <= endDate;
                });

                if (filteredData.length === 0) {
                    setError("No appointments found for the selected date range after filtering.");
                    return;
                }

                // Aggregate treatments performed
                const treatmentCounts = {};
                filteredData.forEach((appointment) => {
                    if (appointment.invoice && Array.isArray(appointment.invoice)) {
                        appointment.invoice.forEach((item) => {
                            const treatmentName = item.treatmentName || item.treatment;
                            if (!treatmentCounts[treatmentName]) {
                                treatmentCounts[treatmentName] = 0;
                            }
                            treatmentCounts[treatmentName] += item.quantity || 1;
                        });
                    }
                });

                reportData = Object.entries(treatmentCounts).map(([treatmentName, count]) => ({
                    treatmentName,
                    count,
                }));
            } else if (reportType === 'Dentists') {
                // First fetch all dentists to check archived status
                const dentistsResponse = await fetch('https://api.hgcdental.clinic/api/dentists');
                if (!dentistsResponse.ok) {
                    throw new Error("Failed to fetch dentists.");
                }
                const allDentists = await dentistsResponse.json();
            
                // Filter out archived dentists
                const activeDentists = allDentists.filter(dentist => !dentist.isArchived);
                const activeDentistNames = activeDentists.map(dentist => dentist.name);
            
                // Fetch appointment data and filter by date
                const response = await fetch(
                    `https://api.hgcdental.clinic/api/appointments?start=${startDate}&end=${endDate}`
                );
            
                if (!response.ok) {
                    throw new Error("Failed to fetch appointments.");
                }
            
                const data = await response.json();
            
                if (!data || data.length === 0) {
                    setError("No appointments found for the selected date range.");
                    return;
                }
            
                // Filter by date (client-side) and only include active dentists
                const filteredData = data.filter(appointment => {
                    const appointmentDate = appointment.date.split("T")[0];
                    const dentistName = appointment.dentist || appointment.dentistName;
                    return (appointmentDate >= startDate && 
                            appointmentDate <= endDate && 
                            activeDentistNames.includes(dentistName));
                });
            
                if (filteredData.length === 0) {
                    setError("No appointments found for active dentists in the selected date range.");
                    return;
                }
            
                // Group appointments by dentist (only active dentists will be included)
                const dentistAppointments = {};
                filteredData.forEach((appointment) => {
                    const dentistName = appointment.dentist || appointment.dentistName;
                    if (!dentistAppointments[dentistName]) {
                        dentistAppointments[dentistName] = 0;
                    }
                    dentistAppointments[dentistName]++;
                });
            
                reportData = Object.entries(dentistAppointments).map(([dentistName, count]) => ({
                    dentistName,
                    count,
                }));
            } else if (reportType === 'Revenue') {
                // Fetch appointment data
                const response = await fetch(
                    `https://api.hgcdental.clinic/api/appointments?start=${startDate}&end=${endDate}`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch appointments.");
                }

                const data = await response.json();

                if (!data || data.length === 0) {
                    setError("No appointments found for the selected date range.");
                    return;
                }

                // Filter by date (client-side)
                const filteredData = data.filter(appointment => {
                    const appointmentDate = appointment.date.split("T")[0];
                    return appointmentDate >= startDate && appointmentDate <= endDate;
                });

                if (filteredData.length === 0) {
                    setError("No appointments found for the selected date range after filtering.");
                    return;
                }

                // Format data for Revenue report
                reportData = filteredData.map((appointment) => {
                    // Calculate Grand Total from invoice items
                    const grandTotal = appointment.invoice?.reduce(
                        (sum, item) => sum + (item.treatmentCost * (item.quantity || 1)),
                        0
                    ) || 0;

                    return {
                        date: appointment.date.split("T")[0], // Format: YYYY-MM-DD
                        patientName: appointment.patientName,
                        grandTotal: grandTotal,
                    };
                });
            } else if (reportType === 'Inventory') {
                // Fetch appointment data with inventory
                const response = await fetch(
                    `https://api.hgcdental.clinic/api/appointments?start=${startDate}&end=${endDate}`
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch appointments.");
                }

                const data = await response.json();

                if (!data || data.length === 0) {
                    setError("No appointments found for the selected date range.");
                    return;
                }

                // Filter by date (client-side)
                const filteredData = data.filter(appointment => {
                    const appointmentDate = appointment.date.split("T")[0];
                    return appointmentDate >= startDate && appointmentDate <= endDate;
                });

                if (filteredData.length === 0) {
                    setError("No appointments found for the selected date range after filtering.");
                    return;
                }

                // Group inventory usage by date and item
                const inventoryUsage = {};

                filteredData.forEach((appointment) => {
                    const appointmentDate = appointment.date.split("T")[0];

                    if (!appointment.inventory || appointment.inventory.length === 0) {
                        return; // Skip if no inventory used
                    }

                    // Initialize date entry if not exists
                    if (!inventoryUsage[appointmentDate]) {
                        inventoryUsage[appointmentDate] = {};
                    }

                    // Sum quantities and costs per item
                    appointment.inventory.forEach((item) => {
                        const itemName = item.name;

                        if (!inventoryUsage[appointmentDate][itemName]) {
                            inventoryUsage[appointmentDate][itemName] = {
                                quantity: 0,
                                cost: 0,
                            };
                        }

                        inventoryUsage[appointmentDate][itemName].quantity += item.quantity;
                        inventoryUsage[appointmentDate][itemName].cost += item.total;
                    });
                });

                // Convert to array format for Excel
                reportData = Object.entries(inventoryUsage).flatMap(([date, items]) => {
                    return Object.entries(items).map(([itemName, usage]) => ({
                        date,
                        itemName,
                        quantityUsed: usage.quantity,
                        totalCost: usage.cost,
                    }));
                });

                if (reportData.length === 0) {
                    setError("No inventory usage found for the selected date range.");
                    return;
                }
            }

            generateExcel(reportData); // Export the data to Excel
        } catch (err) {
            console.error(err);
            setError(err.message || "An error occurred while generating the report.");
        }
    };

    const generateExcel = (data) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report');

        // Add report name at the top
        worksheet.mergeCells('A1:C1');
        const titleRow = worksheet.getCell('A1');
        titleRow.value = `${reportType} Report`;
        titleRow.font = { bold: true, size: 16 };
        titleRow.alignment = { horizontal: 'center' };
        titleRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };

        // Add date range below the title (only if NOT Patients report)
        if (reportType !== 'Patients') {
            worksheet.mergeCells('A2:C2');
            const dateRangeRow = worksheet.getCell('A2');
            dateRangeRow.value = `Date From: ${startDate} | Date To: ${endDate}`;
            dateRangeRow.font = { bold: true, size: 12 };
            dateRangeRow.alignment = { horizontal: 'center' };
            dateRangeRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
        }

        // Define headers based on report type
        let headers;
        if (reportType === 'Appointments') {
            headers = ["Date", "Total Appointments", "Revenue"];
        } else if (reportType === 'Patients') {
            headers = ["Patient ID", "Name", "Sex", "Birthdate", "Age (Years)", "Age (Months)", "Guardian Name", "Address", "Contact Information", "Email"];
        } else if (reportType === 'Treatments') {
            headers = ["Treatment Name", "Count"];
        } else if (reportType === 'Dentists') {
            headers = ["Dentist Name", "Appointments Count"];
        } else if (reportType === 'Revenue') {
            headers = ["Date", "Patient Name", "Grand Total"];
        } else if (reportType === 'Inventory') {
            headers = ["Date", "Inventory Item", "Quantity Used", "Total Cost"];
        }

        // Add styled headers
        const headerRow = worksheet.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA9A9A9' } };
            cell.alignment = { horizontal: 'center' };
        });

        // Add data rows
        data.forEach((row) => {
            const rowData = Object.values(row);
            worksheet.addRow(rowData);
        });

        // Auto-fit columns
        worksheet.columns.forEach((column) => {
            column.width = 20;
        });

        // Generate and download the Excel file
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${reportType.toLowerCase()}_report.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="modal"
            overlayClassName="overlay"
        >
            <div className="modal-flex-div">
                <button className="close-icon" onClick={onRequestClose}>&times;</button>
                <h2 className="h2-modal">Generate Report</h2>
            </div>
            <div className="modal-divider-1" />

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div>
                    <label>Report Type*</label>
                    <select
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                        className="modal-input"
                        required
                    >
                        <option value="Appointments">Appointments</option>
                        <option value="Dentists">Dentists' Appointments</option>
                        <option value="Patients">Patients List</option>
                        <option value="Treatments">Treatments Count</option>
                        <option value="Revenue">Revenue</option>
                        <option value="Inventory">Inventory Usage</option>
                    </select>
                </div>
                {(reportType === 'Appointments' || reportType === 'Treatments' || reportType === 'Dentists' || reportType === 'Revenue' || reportType === 'Inventory') && (
                    <div className="date-range-container">
                        <div className="date-input">
                            <label>Start Date*</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="modal-input"
                                required
                            />
                        </div>
                        <div className="date-input">
                            <label>End Date*</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="modal-input"
                                required
                            />
                        </div>
                    </div>
                )}
                {/* <div className="padding-top" /> */}
                <div className="modal-divider-bottom" />
                {/* <div className="padding-top" /> */}
                <button
                    type="submit"
                    disabled={loading}
                >
                    {loading ? 'Generating...' : 'Generate Report'}
                </button>
            </form>
        </Modal>
    );
};

export default GenerateReport;